'use client';

/**
 * BrandedLoadingSpinner - Smooth CSS-based loading spinner with logo
 * 
 * Uses CSS border animation for smooth, hardware-accelerated spinning
 * Based on the camera initialization spinner design
 * Used in guest meeting join, lobby waiting, and loading states
 */

import React from 'react';
import Image from 'next/image';
import { cn } from '@/lib/utils';
import {
  TA_SHELL_BOOTSTRAP_COMPLETE_KEY,
  isShellBootstrapPending,
  markShellBootstrapComplete,
} from '@/lib/shellBootstrapState';
import { clearAllAppData, clearInstalledPwaCaches, clearIndexedDbDatabases, recoverServiceWorkerAfterCacheReset } from '@/lib/serviceWorkerRegistration';

export { TA_SHELL_BOOTSTRAP_COMPLETE_KEY, isShellBootstrapPending, markShellBootstrapComplete };

export const SHELL_FIRST_START_MESSAGE = 'Preparing for first start';

export const SHELL_FIRST_START_SUBMESSAGE =
  'Setting up storage and offline tools. This can take a bit longer after installing the app or clearing app data.';

interface BrandedLoadingSpinnerProps {
  mode?: 'default' | 'compact' | 'tiny';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  message?: string;
  subMessage?: string;
  showLogo?: boolean;
  showTagline?: boolean;
  /** Custom taglines to rotate through. If not provided, uses default brand taglines */
  taglines?: string[];
  /** Static tagline (no rotation). Takes precedence over taglines array */
  tagline?: string;
  /** Rotation interval in milliseconds (default: 3000) */
  taglineInterval?: number;
  className?: string;
  primaryColor?: string;
  /** Optional determinate progress percentage (0-100). If omitted, progress is indeterminate. */
  progress?: number | null;
  /** Optional width transition duration for determinate progress updates. */
  progressTransitionDurationMs?: number;
  /** Force showing or hiding the progress bar. Defaults to true for richer loading states, false for compact inline spinners. */
  showProgressBar?: boolean;
  /** Force showing or hiding the elevated surface card. Defaults to true for richer loading states, false for compact inline spinners. */
  showSurface?: boolean;
  /**
   * When true, shows {@link SHELL_FIRST_START_MESSAGE} while {@link isShellBootstrapPending} (no prior successful shell bootstrap in storage).
   * After {@link markShellBootstrapComplete}, normal `message` / `subMessage` are used.
   */
  bootstrapShellCopy?: boolean;
  /**
   * When true, suppresses the "Clear Cache & Reload" button that normally
   * appears after 40s on default-mode loaders. Use this for known-long
   * operations (e.g. AI receipt analysis) where the wait is expected and
   * clearing the cache would lose the user's auth token / in-progress data.
   */
  suppressClearCacheButton?: boolean;
}

const isHexColor = (value: string) => /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value.trim());

const normalizeHex = (value: string) => {
  const hex = value.trim();
  if (hex.length === 4) {
    return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  }
  return hex;
};

const mixHexColors = (baseColor: string, mixColor: string, mixAmount: number) => {
  if (!isHexColor(baseColor) || !isHexColor(mixColor)) {
    return baseColor;
  }

  const base = normalizeHex(baseColor).replace('#', '');
  const mix = normalizeHex(mixColor).replace('#', '');
  const ratio = Math.max(0, Math.min(1, mixAmount));

  const channels = [0, 2, 4].map((index) => {
    const baseValue = parseInt(base.slice(index, index + 2), 16);
    const mixValue = parseInt(mix.slice(index, index + 2), 16);
    const blended = Math.round(baseValue + (mixValue - baseValue) * ratio);
    return blended.toString(16).padStart(2, '0');
  });

  return `#${channels.join('')}`;
};

const withHexAlpha = (color: string, alpha: number) => {
  if (!isHexColor(color)) {
    return color;
  }

  const normalized = normalizeHex(color);
  const alphaHex = Math.round(Math.max(0, Math.min(1, alpha)) * 255)
    .toString(16)
    .padStart(2, '0');

  return `${normalized}${alphaHex}`;
};

// Default rotating taglines for brand messaging
const DEFAULT_TAGLINES = [
  "Total Solutions. Total Control.",
  "One Platform. Complete Control.",
  "All-in-One Enterprise Solutions",
  "The Complete Business Platform",
  "Cloud-Powered Business",
];

export default function BrandedLoadingSpinner({
  mode: incomingMode = 'default',
  size = 'md',
  message,
  subMessage,
  showLogo = true,
  showTagline = false,
  taglines,
  tagline,
  taglineInterval = 3000,
  className,
  primaryColor = '#0f65fa',
  progress = null,
  progressTransitionDurationMs = 300,
  showProgressBar,
  showSurface,
  bootstrapShellCopy = false,
  suppressClearCacheButton = false,
}: BrandedLoadingSpinnerProps) {
  // Fresh read every render (no useState) so copy tracks localStorage immediately and SSR/hydration
  // never sticks on "idle" where shellFirstStart was false until an effect ran.
  // SSR renders the caller's own message — server-side there is no
  // localStorage, so defaulting to first-start copy made every public page
  // flash "Preparing for first start" before hydration. Client-side,
  // isShellBootstrapPending() is the only gate (still true until the
  // signed-in shell's first load completes).
  const shellFirstStart =
    bootstrapShellCopy &&
    typeof window !== 'undefined' &&
    isShellBootstrapPending();
  const displayMessage = shellFirstStart ? SHELL_FIRST_START_MESSAGE : message;
  const displaySubMessage = shellFirstStart ? SHELL_FIRST_START_SUBMESSAGE : subMessage;

  const isCapacitor = typeof window !== 'undefined' && typeof (window as any).Capacitor !== 'undefined';
  // Use the full "default" treatment on native for full-page loaders only. Inline patterns
  // (compact / tiny) must stay compact so CardLoadingRow / InlineLoadingPill keep spinner + label side-by-side.
  const mode =
    incomingMode === 'compact' || incomingMode === 'tiny'
      ? incomingMode
      : isCapacitor
        ? 'default'
        : incomingMode;
  // Use custom taglines or defaults
  const activeTaglines = taglines || DEFAULT_TAGLINES;

  // Rotating tagline state
  const [taglineIndex, setTaglineIndex] = React.useState(0);

  // Simulated progress state for indeterminate mode
  const [simulatedProgress, setSimulatedProgress] = React.useState(0);

  // Reset / clear data state (only used during shell first-start)
  const [isResetting, setIsResetting] = React.useState(false);
  const [showResetButton, setShowResetButton] = React.useState(false);
  const [resetElapsed, setResetElapsed] = React.useState(0);

  // Extended loading clear-cache state (shown after 20s on any loader, not just first-start)
  const [showClearCacheButton, setShowClearCacheButton] = React.useState(false);
  const [isClearingCache, setIsClearingCache] = React.useState(false);
  const [clearCacheElapsed, setClearCacheElapsed] = React.useState(0);

  // Show the reset button after 15 seconds of being stuck on first-start
  React.useEffect(() => {
    if (!shellFirstStart) {
      setShowResetButton(false);
      setResetElapsed(0);
      return;
    }

    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setResetElapsed(elapsed);
      if (elapsed >= 15) {
        setShowResetButton(true);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [shellFirstStart]);

  // Show a clear-cache button after 40s on any default-mode loader (not just first-start).
  // This catches cases where the app hangs due to stale SW, expired tokens, or deployment mismatches.
  // Suppressed when suppressClearCacheButton is set (e.g. AI receipt analysis where long waits
  // are expected and clearing the cache would lose the user's auth token / in-progress data).
  React.useEffect(() => {
    // Only show on full-page loaders (default mode) with messages, not compact/tiny inline spinners.
    // Also skip if shellFirstStart is active (the reset button already covers that case).
    if (shellFirstStart || mode !== 'default' || !displayMessage || suppressClearCacheButton) {
      setShowClearCacheButton(false);
      setClearCacheElapsed(0);
      return;
    }

    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setClearCacheElapsed(elapsed);
      if (elapsed >= 40) {
        setShowClearCacheButton(true);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [shellFirstStart, mode, displayMessage, suppressClearCacheButton]);

  const handleClearCacheAndReload = React.useCallback(async () => {
    if (isClearingCache) return;
    setIsClearingCache(true);
    try {
      console.log('[BrandedLoadingSpinner] User triggered clear cache & reload');
      await Promise.race([
        Promise.all([
          clearInstalledPwaCaches(),
          clearIndexedDbDatabases(),
        ]),
        new Promise<void>((resolve) =>
          window.setTimeout(() => {
            console.warn('[BrandedLoadingSpinner] clearInstalledPwaCaches + clearIndexedDbDatabases timed out after 10s, reloading');
            resolve();
          }, 10000),
        ),
      ]);
      await recoverServiceWorkerAfterCacheReset();
      await new Promise(resolve => setTimeout(resolve, 500));
      window.location.reload();
    } catch (error) {
      console.error('[BrandedLoadingSpinner] Clear cache failed:', error);
      window.location.reload();
    }
  }, [isClearingCache]);

  const handleResetAndClearData = React.useCallback(async () => {
    if (isResetting) return;
    setIsResetting(true);
    try {
      console.log('[BrandedLoadingSpinner] User triggered reset & clear data');
      // Clear the bootstrap flag so we don't loop back into first-start
      try {
        window.localStorage.removeItem(TA_SHELL_BOOTSTRAP_COMPLETE_KEY);
      } catch {
        // ignore
      }
      // Full data clear (IndexedDB, caches, storage) with a hard 15s timeout
      // so the user is never stuck on "Clearing data…" indefinitely
      await Promise.race([
        clearAllAppData(),
        new Promise<void>((resolve) =>
          window.setTimeout(() => {
            console.warn('[BrandedLoadingSpinner] clearAllAppData timed out after 15s, force-reloading');
            resolve();
          }, 15000),
        ),
      ]);
      // Hard reload to re-bootstrap
      window.location.reload();
    } catch (error) {
      console.error('[BrandedLoadingSpinner] Reset & clear data failed:', error);
      // Still try to reload
      window.location.reload();
    }
  }, [isResetting]);

  React.useEffect(() => {
    // Don't rotate if static tagline is provided or showTagline is false
    if (!showTagline || tagline) return;

    const interval = setInterval(() => {
      setTaglineIndex((prev) => (prev + 1) % activeTaglines.length);
    }, taglineInterval);

    return () => clearInterval(interval);
  }, [showTagline, tagline, activeTaglines.length, taglineInterval]);

  // Simulate progress when no determinate progress is provided.
  // Uses asymptotic slowdown: starts fast, slows near 90%, never reaches 100%.
  // This avoids a jarring backward slide caused by the CSS width transition.
  const isDeterminate = progress !== null;
  React.useEffect(() => {
    if (isDeterminate) {
      return;
    }

    // Reset and start fresh when entering indeterminate mode
    setSimulatedProgress(0);

    const interval = setInterval(() => {
      setSimulatedProgress((prev) => {
        // Remaining distance to 92% ceiling, scaled down for gradual approach
        const remaining = 92 - prev;
        if (remaining <= 0.5) return prev; // effectively parked
        const increment = Math.max(0.5, remaining * (0.08 + Math.random() * 0.12));
        return Math.min(prev + increment, 92);
      });
    }, 800);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when switching between determinate/indeterminate, not on every progress value change
  }, [isDeterminate]);

  // Size configurations - logo size, ring size, border width
  const sizeConfig = {
    xs: { logo: 22, ring: 30, border: 2, ringOffset: 4 },
    sm: { logo: 32, ring: 44, border: 3, ringOffset: 6 },
    md: { logo: 48, ring: 64, border: 3, ringOffset: 8 },
    lg: { logo: 64, ring: 88, border: 4, ringOffset: 12 },
    xl: { logo: 80, ring: 108, border: 4, ringOffset: 14 }
  };

  const compactSizeConfig = {
    xs: { logo: 24, ring: 34, border: 2, ringOffset: 4 },
    sm: { logo: 28, ring: 40, border: 2, ringOffset: 5 },
    md: { logo: 34, ring: 48, border: 3, ringOffset: 6 },
    lg: { logo: 42, ring: 58, border: 3, ringOffset: 8 },
    xl: { logo: 52, ring: 72, border: 4, ringOffset: 10 },
  };

  const tinyConfig = { logo: 24, ring: 34, border: 2, ringOffset: 5 };

  const isCompactMode = mode === 'compact';
  const config = mode === 'tiny'
    ? tinyConfig
    : mode === 'compact'
      ? compactSizeConfig[size]
      : sizeConfig[size];
  const hasContextText = mode !== 'tiny' && Boolean(displayMessage || displaySubMessage || showTagline);
  // Rich surface + progress on native for full default-mode loaders; explicit showSurface/showProgressBar always win.
  const useSurface = mode === 'tiny'
    ? false
    : showSurface !== undefined
      ? showSurface
      : isCapacitor && mode === 'default'
        ? true
        : mode === 'default'
          ? hasContextText
          : false;
  const useProgressBar = mode === 'tiny'
    ? false
    : showProgressBar !== undefined
      ? showProgressBar
      : isCapacitor && mode === 'default'
        ? true
        : mode === 'default'
          ? useSurface
          : false;
  const shouldShowLogo = showLogo;
  const clampedProgress = typeof progress === 'number'
    ? Math.max(0, Math.min(100, progress))
    : simulatedProgress;
  const progressFillGradient = isHexColor(primaryColor)
    ? `linear-gradient(90deg, ${mixHexColors(primaryColor, '#0f65fa', 0.18)} 0%, ${mixHexColors(primaryColor, '#0f65fa', 0.52)} 55%, ${mixHexColors(primaryColor, '#ffffff', 0.2)} 100%)`
    : `linear-gradient(90deg, ${primaryColor} 0%, #0f65fa 55%, ${primaryColor} 100%)`;
  const spinnerGlow = isHexColor(primaryColor)
    ? `0 0 24px ${withHexAlpha(primaryColor, 0.18)}, 0 8px 24px rgba(15, 23, 42, 0.08)`
    : '0 0 24px rgba(15, 101, 250, 0.18), 0 8px 24px rgba(15, 23, 42, 0.08)';
  const outerRingColor = isHexColor(primaryColor) ? withHexAlpha(primaryColor, 0.18) : 'rgba(15, 101, 250, 0.18)';
  const trackRingColor = isHexColor(primaryColor) ? withHexAlpha(primaryColor, 0.1) : 'rgba(148, 163, 184, 0.16)';

  const spinnerCore = (
    <>
      {/* Spinner with logo */}
      <div
        className="relative mx-auto flex items-center justify-center"
        style={{ width: config.ring, height: config.ring }}
      >
        {/* Center logo with subtle glow */}
        {shouldShowLogo && (
          <div
            className="relative z-10 rounded-full bg-white dark:bg-slate-900 flex items-center justify-center"
            style={{
              width: config.logo + (mode === 'tiny' ? 6 : 8),
              height: config.logo + (mode === 'tiny' ? 6 : 8),
              boxShadow: mode === 'tiny'
                ? `0 0 12px ${withHexAlpha(primaryColor, 0.14)}`
                : spinnerGlow,
            }}
          >
            <Image
              src="/logo/total-access-icon.png"
              alt="TotalAccess"
              width={config.logo}
              height={config.logo}
              className="object-contain"
              priority
              unoptimized
            />
          </div>
        )}

        {/* Smooth CSS border spinner ring */}
        <div
          className="absolute inset-0 rounded-full animate-spin"
          style={{
            borderWidth: config.border,
            borderStyle: 'solid',
            borderColor: 'transparent',
            borderTopColor: primaryColor,
            borderRightColor: primaryColor,
            boxShadow: `0 0 0 1px ${outerRingColor}`,
            animationDuration: '1s',
            animationTimingFunction: 'linear',
          }}
        />

        {/* Secondary subtle ring for depth */}
        <div
          className="absolute inset-0 rounded-full"
          style={{
            borderWidth: config.border,
            borderStyle: 'solid',
            borderColor: trackRingColor,
          }}
        />
      </div>

      {/* Messages */}
      {mode !== 'tiny' && (displayMessage || displaySubMessage) && (
        <div className={cn('text-center', useSurface ? 'mt-6' : isCompactMode ? 'mt-3' : 'mt-4')}>
          {displayMessage && (
            <p
              className={cn(
                useSurface
                  ? 'text-xl font-semibold text-slate-900 dark:text-white'
                  : isCompactMode
                    ? 'text-sm font-semibold text-gray-800 dark:text-white'
                    : 'text-base font-semibold text-gray-800 dark:text-white'
              )}
              suppressHydrationWarning={Boolean(bootstrapShellCopy)}
            >
              {displayMessage}
            </p>
          )}
          {displaySubMessage && (
            <p
              className={cn(
                useSurface
                  ? 'mt-3 text-sm leading-6 text-slate-500 dark:text-slate-300'
                  : isCompactMode
                    ? 'mt-1 text-xs text-gray-500 dark:text-gray-400'
                    : 'mt-1 text-sm text-gray-500 dark:text-gray-400'
              )}
              suppressHydrationWarning={Boolean(bootstrapShellCopy)}
            >
              {displaySubMessage}
            </p>
          )}
        </div>
      )}

      {/* Rotating or Static Tagline */}
      {mode !== 'tiny' && showTagline && (
        <div className={cn('text-center overflow-hidden', useSurface ? 'mt-3 h-6' : isCompactMode ? 'mt-2 h-5' : 'mt-3 h-6')}>
          <p
            key={tagline ? 'static' : taglineIndex}
            className={cn('font-medium italic', isCompactMode ? 'text-xs' : 'text-sm')}
            style={{
              color: primaryColor,
              animation: tagline ? 'none' : 'fadeInUp 0.5s ease-out'
            }}
          >
            {tagline || activeTaglines[taglineIndex]}
          </p>
        </div>
      )}

      {/* Progress Bar - simulated progress for indeterminate mode */}
      {mode !== 'tiny' && useProgressBar && (
        <div className={cn('w-full', useSurface ? 'mt-5' : 'mt-4')}>
          <div className={cn(
            'relative h-2 overflow-hidden rounded-full ring-1',
            useSurface
              ? 'bg-slate-900/5 ring-slate-900/8 dark:bg-white/10 dark:ring-white/10'
              : 'bg-slate-900/8 ring-slate-900/8 dark:bg-white/10 dark:ring-white/10'
          )}>
            {isDeterminate ? (
              // Determinate progress with actual value
              <>
                <div
                  className="absolute inset-y-0 left-0 rounded-full shadow-[0_0_18px_rgba(96,165,250,0.28)]"
                  style={{
                    width: `${clampedProgress}%`,
                    background: progressFillGradient,
                    transition: `width ${progressTransitionDurationMs}ms ease-out`,
                  }}
                />
              </>
            ) : (
              // Simulated progress for indeterminate mode
              <>
                <div
                  className="loading-progress-stream pointer-events-none absolute inset-0"
                  style={{
                    backgroundImage: `linear-gradient(90deg, transparent 0%, ${withHexAlpha(primaryColor, 0.08)} 18%, ${withHexAlpha(primaryColor, 0.18)} 50%, ${withHexAlpha(primaryColor, 0.08)} 82%, transparent 100%)`,
                    backgroundSize: '180% 100%',
                  }}
                />
                <div
                  className="absolute inset-y-0 left-0 rounded-full shadow-[0_0_18px_rgba(96,165,250,0.28)]"
                  style={{
                    width: `${clampedProgress}%`,
                    background: progressFillGradient,
                    transition: 'width 0.6s ease-out',
                  }}
                />
                <div className="loading-progress-shimmer pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-transparent via-white/35 to-transparent mix-blend-screen" />
              </>
            )}
          </div>
        </div>
      )}

      {/* Reset & Clear Data button — only during shell first-start after 15s */}
      {shellFirstStart && showResetButton && (
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => void handleResetAndClearData()}
            disabled={isResetting}
            className="inline-flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-800 dark:bg-red-950/50 dark:text-red-400 dark:hover:bg-red-950"
          >
            {isResetting ? (
              <>
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Clearing data…
              </>
            ) : (
              <>
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Reset &amp; Clear Data
              </>
            )}
          </button>
          {isResetting && resetElapsed - 15 >= 10 && (
            <p className="mt-2 text-xs text-amber-500 dark:text-amber-400">
              Taking longer than expected — will reload shortly…
            </p>
          )}
          {!isResetting && resetElapsed >= 15 && (
            <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">
              Still loading after {resetElapsed}s? Try resetting.
            </p>
          )}
        </div>
      )}

      {/* Clear Cache & Reload button — shown after 40s on any default-mode loader (not just first-start) */}
      {!shellFirstStart && showClearCacheButton && (
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => void handleClearCacheAndReload()}
            disabled={isClearingCache}
            className="inline-flex items-center gap-2 rounded-lg border border-blue-300 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-blue-800 dark:bg-blue-950/50 dark:text-blue-400 dark:hover:bg-blue-950"
          >
            {isClearingCache ? (
              <>
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Clearing cache…
              </>
            ) : (
              <>
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Clear Cache &amp; Reload
              </>
            )}
          </button>
          {!isClearingCache && clearCacheElapsed >= 40 && (
            <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">
              Still loading after {clearCacheElapsed}s? Try clearing your cache.
            </p>
          )}
        </div>
      )}
    </>
  );

  return (
    <div className={cn('flex flex-col items-center justify-center', mode === 'tiny' ? 'leading-none' : undefined, className)}>
      {useSurface ? (
        <div className="w-full max-w-md rounded-3xl border border-black/5 bg-white/95 p-6 text-center shadow-[0_24px_60px_rgba(15,23,42,0.16)] backdrop-blur-sm dark:border-white/10 dark:bg-slate-900/95 dark:shadow-[0_24px_60px_rgba(2,6,23,0.5)] sm:p-8">
          {spinnerCore}
        </div>
      ) : (
        spinnerCore
      )}

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes indeterminateSlide {
          0% {
            transform: translateX(-165%) scaleX(0.42);
            opacity: 0.35;
          }
          38% {
            transform: translateX(-12%) scaleX(0.98);
            opacity: 0.95;
          }
          68% {
            transform: translateX(68%) scaleX(0.74);
            opacity: 0.9;
          }
          100% {
            transform: translateX(238%) scaleX(0.52);
            opacity: 0.4;
          }
        }

        @keyframes indeterminateStream {
          0% {
            background-position: 180% 0;
            opacity: 0.35;
          }
          50% {
            opacity: 0.7;
          }
          100% {
            background-position: -160% 0;
            opacity: 0.35;
          }
        }

        @keyframes progressShimmer {
          0% {
            transform: translateX(-130%);
            opacity: 0;
          }
          18% {
            opacity: 0.9;
          }
          100% {
            transform: translateX(360%);
            opacity: 0;
          }
        }

        :global(.loading-progress-indeterminate) {
          animation: indeterminateSlide 1.8s ease-in-out infinite;
          will-change: transform, opacity;
        }

        :global(.loading-progress-indeterminate-secondary) {
          animation-delay: 0.9s;
          animation-duration: 2.15s;
        }

        :global(.loading-progress-stream) {
          animation: indeterminateStream 2.6s linear infinite;
        }

        :global(.loading-progress-shimmer) {
          animation: progressShimmer 1.9s ease-out infinite;
        }
      `}</style>
    </div>
  );
}

// ============================================================================
// CONVENIENCE PATTERN COMPONENTS
// Four named exports that encapsulate the recurring loading patterns used
// throughout the codebase so callsites stay readable and consistent.
// ============================================================================

/**
 * InlineLoadingPill — rounded-full pill capsule with spinner + label.
 * Use inside data-grid cells, tab headers, or any tight horizontal space.
 *
 * @example
 * <InlineLoadingPill label="Loading relationships..." primaryColor="#6366f1" />
 */
interface InlineLoadingPillProps {
  label: string;
  primaryColor?: string;
  className?: string;
}

export function InlineLoadingPill({ label, primaryColor = '#0f65fa', className }: InlineLoadingPillProps) {
  return (
    <div className={cn('flex items-center justify-center', className)}>
      <div className="rounded-full border border-gray-200 bg-white/90 px-4 py-2 shadow-sm dark:border-gray-700 dark:bg-gray-900/70">
        <div className="flex items-center gap-3">
          <BrandedLoadingSpinner mode="compact" size="sm" primaryColor={primaryColor} showSurface={false} showProgressBar={false} />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * CardLoadingRow — rounded-2xl card with spinner + label.
 * Use for view-level loading states (Kanban, dashboards, panels).
 *
 * @example
 * <CardLoadingRow label="Loading view..." primaryColor="#2563eb" />
 */
interface CardLoadingRowProps {
  label: string;
  primaryColor?: string;
  className?: string;
}

export function CardLoadingRow({ label, primaryColor = '#0f65fa', className }: CardLoadingRowProps) {
  return (
    <div className={cn('flex items-center justify-center', className)}>
      <div className="rounded-2xl border border-gray-100 bg-white/95 px-5 py-4 shadow-sm dark:border-gray-700/60 dark:bg-gray-900/80">
        <div className="flex items-center gap-3">
          <BrandedLoadingSpinner mode="tiny" size="sm" primaryColor={primaryColor} showSurface={false} showProgressBar={false} />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * SectionLoader — centred spinner with optional message for section-level loading.
 * Replaces the pattern of wrapping BrandedLoadingSpinner in a centred div with
 * showSurface={false}.
 *
 * @example
 * <SectionLoader message="Loading settings..." subMessage="Preparing configuration." primaryColor="#2563eb" />
 */
interface SectionLoaderProps {
  message?: string;
  subMessage?: string;
  primaryColor?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  progress?: number | null;
  tagline?: string;
  showTagline?: boolean;
  className?: string;
  bootstrapShellCopy?: boolean;
}

export function SectionLoader({
  message,
  subMessage,
  primaryColor = '#0f65fa',
  size = 'md',
  progress,
  tagline,
  showTagline,
  className,
  bootstrapShellCopy,
}: SectionLoaderProps) {
  return (
    <div className={cn('flex items-center justify-center', className)}>
      <BrandedLoadingSpinner
        size={size}
        message={message}
        subMessage={subMessage}
        primaryColor={primaryColor}
        progress={progress}
        tagline={tagline}
        showTagline={showTagline}
        showSurface={false}
        showProgressBar={Boolean(message) || Boolean(bootstrapShellCopy)}
        bootstrapShellCopy={bootstrapShellCopy}
      />
    </div>
  );
}

/**
 * PageLoader — full-page centred loading screen with surface card.
 * Use for top-level route transitions and module initialisation.
 *
 * @example
 * <PageLoader message="Loading your workspace..." primaryColor="#2563eb" />
 */
interface PageLoaderProps {
  message?: string;
  subMessage?: string;
  primaryColor?: string;
  progress?: number | null;
  tagline?: string;
  showTagline?: boolean;
  className?: string;
  bootstrapShellCopy?: boolean;
  /** Suppress the "Clear Cache & Reload" button for known-long operations (AI analysis, etc.) */
  suppressClearCacheButton?: boolean;
}

export function PageLoader({
  message,
  subMessage,
  primaryColor = '#0f65fa',
  progress,
  tagline,
  showTagline,
  className,
  bootstrapShellCopy,
  suppressClearCacheButton,
}: PageLoaderProps) {
  return (
    <div className={cn('fixed inset-0 z-[9999] flex items-center justify-center bg-white dark:bg-gray-900', className)}>
      <BrandedLoadingSpinner
        size="lg"
        message={message}
        subMessage={subMessage}
        primaryColor={primaryColor}
        progress={progress}
        tagline={tagline}
        showTagline={showTagline}
        showSurface={Boolean(message || subMessage || bootstrapShellCopy)}
        showProgressBar
        bootstrapShellCopy={bootstrapShellCopy}
        suppressClearCacheButton={suppressClearCacheButton}
      />
    </div>
  );
}

// ============================================================================

// Branded avatar icon - static version for use as avatars/icons
interface BrandedAvatarProps {
  size?: number;
  className?: string;
  primaryColor?: string;
}

export function BrandedAvatar({
  size = 40,
  className,
  primaryColor = '#00affa'
}: BrandedAvatarProps) {
  return (
    <div
      className={cn(
        'rounded-full flex items-center justify-center',
        className
      )}
      style={{
        width: size,
        height: size,
        backgroundColor: `${primaryColor}15`,
        boxShadow: `0 0 10px ${primaryColor}20`
      }}
    >
      <Image
        src="/logo/total-access-icon.png"
        alt="TotalAccess"
        width={Math.floor(size * 0.6)}
        height={Math.floor(size * 0.6)}
        className="object-contain"
        unoptimized
      />
    </div>
  );
}

// Standalone QR code with embedded logo
interface BrandedQRCodeProps {
  value: string;
  size?: number;
  primaryColor?: string;
  className?: string;
}

export function BrandedQRCode({
  value,
  size = 160,
  primaryColor = '#0f65fa',
  className
}: BrandedQRCodeProps) {
  // Import QRCodeSVG dynamically to avoid SSR issues
  const QRCodeSVG = require('qrcode.react').QRCodeSVG;
  
  const logoSize = Math.floor(size * 0.25);

  return (
    <div className={cn('relative inline-block', className)}>
      {/* QR Code with colors */}
      <div 
        className="p-3 rounded-2xl bg-white shadow-lg"
        style={{ boxShadow: `0 4px 20px ${primaryColor}20` }}
      >
        <QRCodeSVG
          value={value}
          size={size}
          level="H" // High error correction to allow logo overlay
          includeMargin={false}
          fgColor="#1f2937"
          bgColor="transparent"
          imageSettings={{
            src: '/logo/total-access-icon.png',
            height: logoSize,
            width: logoSize,
            excavate: true, // Clear area for logo
          }}
        />
      </div>
      
      {/* Decorative corner accents */}
      <div 
        className="absolute -top-1 -left-1 w-4 h-4 border-t-2 border-l-2 rounded-tl-lg"
        style={{ borderColor: primaryColor }}
      />
      <div 
        className="absolute -top-1 -right-1 w-4 h-4 border-t-2 border-r-2 rounded-tr-lg"
        style={{ borderColor: primaryColor }}
      />
      <div 
        className="absolute -bottom-1 -left-1 w-4 h-4 border-b-2 border-l-2 rounded-bl-lg"
        style={{ borderColor: primaryColor }}
      />
      <div 
        className="absolute -bottom-1 -right-1 w-4 h-4 border-b-2 border-r-2 rounded-br-lg"
        style={{ borderColor: primaryColor }}
      />
    </div>
  );
}

// Full-screen loading overlay
interface LoadingOverlayProps {
  isVisible: boolean;
  message?: string;
  subMessage?: string;
  primaryColor?: string;
}

export function LoadingOverlay({
  isVisible,
  message = 'Loading...',
  subMessage,
  primaryColor = '#0f65fa'
}: LoadingOverlayProps) {
  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-gray-900/95 via-gray-800/95 to-gray-900/95 backdrop-blur-sm">
      <BrandedLoadingSpinner
        size="xl"
        message={message}
        subMessage={subMessage}
        primaryColor={primaryColor}
      />
    </div>
  );
}
